import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { PeekStack } from "@piiqtechnologies/ui-components";
import { CustomSubtopicCard } from "../../custom-subtopics-card";
import messages from "./messages.i18n";
const COMMUTE_PATTERN = /^(\d+)\s+minutes\s+by\s+(.+?)\s+to\s+(.+)$/;
// Backend consensus emits modes as the LLM wrote them ("public transit"); FE
// canonical form is hyphenated ("public-transit"). Normalize on read so the
// mode survives the round-trip.
const normalizeTransportMode = (raw) => raw.toLowerCase().trim().replace(/\s+/g, "-");
const parseCommuteStrings = (strings) => strings.reduce((items, s) => {
    const match = s.match(COMMUTE_PATTERN);
    return match
        ? [
            ...items,
            { minutes: Number(match[1]), transportMode: normalizeTransportMode(match[2]), destination: match[3] },
        ]
        : items;
}, []);
export const CommuteTabContent = ({ maxAddedSubtopics, otherTabsAddedCount = 0, baseSubtopicsData, onAddedCountChange, onSubtopicsDataChange, saveTrigger, onEditingCountChange, initialData, readOnly, hasBeenRun, }) => {
    const intl = useIntl();
    const addedCountsRef = useRef({});
    const subtopicsDataRef = useRef({});
    const editingCountsRef = useRef({});
    const [totalAdded, setTotalAdded] = useState(0);
    const [totalEditing, setTotalEditing] = useState(0);
    const createAddedCountHandler = useCallback((cardKey) => (count) => {
        addedCountsRef.current[cardKey] = count;
        const newTotal = Object.values(addedCountsRef.current).reduce((sum, c) => sum + c, 0);
        setTotalAdded(newTotal);
    }, []);
    const createCommuteItemsHandler = useCallback((cardKey) => (items) => {
        subtopicsDataRef.current[cardKey] = items.map((item) => `${item.minutes} minutes by ${item.transportMode} to ${item.destination}`);
        onSubtopicsDataChange === null || onSubtopicsDataChange === void 0 ? void 0 : onSubtopicsDataChange(Object.assign({}, subtopicsDataRef.current));
    }, [onSubtopicsDataChange]);
    const createEditingCountHandler = useCallback((cardKey) => (count) => {
        editingCountsRef.current[cardKey] = count;
        const newTotal = Object.values(editingCountsRef.current).reduce((sum, c) => sum + c, 0);
        setTotalEditing(newTotal);
    }, []);
    const commuteAddedCount = useMemo(() => createAddedCountHandler("commute"), [createAddedCountHandler]);
    const commuteItemsChange = useMemo(() => createCommuteItemsHandler("commute"), [createCommuteItemsHandler]);
    const commuteEditing = useMemo(() => createEditingCountHandler("commute"), [createEditingCountHandler]);
    useEffect(() => {
        onAddedCountChange === null || onAddedCountChange === void 0 ? void 0 : onAddedCountChange(totalAdded);
    }, [totalAdded, onAddedCountChange]);
    useEffect(() => {
        onEditingCountChange === null || onEditingCountChange === void 0 ? void 0 : onEditingCountChange(totalEditing);
    }, [totalEditing, onEditingCountChange]);
    const canAddMore = totalAdded + otherTabsAddedCount < maxAddedSubtopics;
    return (React.createElement(PeekStack, { direction: "row", gap: 1.5, flex: 1, overflow: "hidden" },
        React.createElement(CustomSubtopicCard, { variant: "commute", title: intl.formatMessage(messages.commuteTitle), subtitle: intl.formatMessage(messages.commuteSubtitle), initialCommuteItems: (initialData === null || initialData === void 0 ? void 0 : initialData.commute) ? parseCommuteStrings(initialData.commute) : [], baseCount: (baseSubtopicsData === null || baseSubtopicsData === void 0 ? void 0 : baseSubtopicsData.commute) ? parseCommuteStrings(baseSubtopicsData.commute).length : 0, canAddMore: canAddMore, maxSubtopics: maxAddedSubtopics, onAddedCountChange: commuteAddedCount, onCommuteItemsChange: commuteItemsChange, saveTrigger: saveTrigger, onEditingCountChange: commuteEditing, readOnly: readOnly, hasBeenRun: hasBeenRun })));
};
